import { useEffect, useState, type ComponentType } from "react";
import { Button } from "primereact/button";

interface RetryableAfsPageProps {
  loadPage: () => Promise<ComponentType>;
  label: string;
  loadingMessage?: string;
  initiallyFailed?: boolean;
}

/** Retry a failed import in place. Reloading is always an explicit user action. */
export function RetryableAfsPage({
  loadPage,
  label,
  loadingMessage = `Loading ${label}…`,
  initiallyFailed = false,
}: RetryableAfsPageProps) {
  const [Page, setPage] = useState<ComponentType | null>(null);
  const [failed, setFailed] = useState(initiallyFailed);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (initiallyFailed && attempt === 0) return;
    let active = true;
    setPage(null);
    setFailed(false);
    Promise.resolve().then(loadPage).then(page => {
      if (active) setPage(() => page);
    }).catch(() => {
      if (active) setFailed(true);
    });
    return () => { active = false; };
  }, [loadPage, attempt, initiallyFailed]);

  if (Page) return <Page />;
  return (
    <div className="flex flex-column align-items-center justify-content-center gap-3 p-5" aria-busy={!failed}>
      {failed ? <>
        <p role="alert">{label} could not be loaded. Check your connection and try again.</p>
        <p>If this continues, reload the portal to get the latest version.</p>
        <div className="flex gap-2">
          <Button label="Retry" icon="pi pi-refresh" onClick={() => setAttempt(value => value + 1)} />
          <Button label="Reload portal" onClick={() => window.location.reload()} />
        </div>
      </> : <p role="status">{loadingMessage}</p>}
    </div>
  );
}
